import { cn } from "../../utils";
import { Typography } from "../Typography";

export type ProgressBarVariant = "primary" | "secondary" | "tertiary";

export interface ProgressBarProps {
  value: number;
  max: number;
  /**
   * Optional template string to customize the progress text display.
   * Use "0" as a placeholder which will be replaced with the actual progress (e.g., "50/100").
   * @example
   *   With template="Progress: 0"
   *   If value=50 and max=100
   *   Display will show: "Progress: 50/100"
   *
   *   Without template
   *   Display will show: "50/100"
   */
  template?: string;
  className?: string;
  barClassName?: string;
  isTextHidden?: boolean;
  variant?: ProgressBarVariant;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max,
  template,
  className,
  barClassName,
  isTextHidden = false,
  variant = "primary",
}) => {
  const clampedValue = Math.min(Math.max(value, 0), max);
  const progress = `${clampedValue}/${max}`;
  const progressPercentage = Math.round((clampedValue / max) * 100);
  const progressText = template?.replace("0", progress) ?? progress;
  const style = { width: `${progressPercentage}%` };
  const isSecondary = variant === "secondary";
  const isTertiary = variant === "tertiary";
  const fillClassName = cn("ui:h-full ui:rounded-full ui:transition-[width]", {
    "ui:bg-[linear-gradient(to_right,#6277FF,#E560FF)]": isTertiary,
    "ui:bg-[linear-gradient(to_right,#203096,#3550FC)]": !isTertiary,
  });

  return (
    <div
      className={cn(
        "ui:grid ui:w-full ui:place-items-center ui:gap-1",
        className,
      )}
    >
      {!isTextHidden && (
        <Typography grotesk className="ui:whitespace-nowrap">
          {progressText}
        </Typography>
      )}
      <div
        className={cn(
          "ui:h-2.5 ui:w-full ui:rounded-full ui:bg-divider",
          {
            "ui:h-3.25 ui:border ui:border-transparent ui:bg-[#f0f0f0] ui:dark:border-divider ui:dark:bg-transparent":
              isSecondary,
          },
          barClassName,
        )}
      >
        {isSecondary && (
          <div
            style={style}
            className="ui:relative ui:bottom-px ui:-left-px ui:h-[13px] ui:rounded-full ui:bg-[linear-gradient(to_right,#6674E9,#C7A9FF)] ui:p-px ui:transition-[width]"
          >
            <div className="ui:h-full ui:w-full ui:rounded-full ui:bg-[linear-gradient(to_right,#3345E2,#9054FF)]" />
          </div>
        )}
        {!isSecondary && <div style={style} className={fillClassName} />}
      </div>
    </div>
  );
};
